import { Tooltip } from '@ads/components-react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import type { ReactNode } from 'react';

export interface BannerFormSectionProps {
  readonly title: string;
  /** Explicação da seção — exibida em tooltip no ícone ao lado do título. */
  readonly hint?: string;
  /** Conteúdo à direita do título (ex.: seletor de mídia). */
  readonly action?: ReactNode;
  /**
   * Âncora `data-field-error` da seção — usada quando o erro de validação é da
   * seção inteira (Turmas, Mídia) e não de um campo isolado.
   */
  readonly errorAnchor?: string;
  readonly children: ReactNode;
}

/**
 * Card de seção do formulário de Banner (Informações, Exibição, Turmas, Mídia).
 *
 * Cabeçalho com título, explicação opcional em tooltip e slot de ação à direita,
 * separado do conteúdo por uma régua — o mesmo ritmo dos cards do protótipo. A
 * explicação vira tooltip para o formulário não virar um muro de texto.
 */
export function BannerFormSection({
  title,
  hint,
  action,
  errorAnchor,
  children,
}: BannerFormSectionProps) {
  return (
    <section
      className="flex flex-col rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface shadow-level1"
      {...(errorAnchor !== undefined ? { 'data-field-error': errorAnchor } : {})}
    >
      <header className="flex flex-wrap items-center justify-between gap-mini border-b-hairline border-ctx-layout-border px-xxs py-micro">
        <div className="flex items-center gap-quark">
          <h2 className="ts-heading-xs text-ctx-content-title">{title}</h2>
          {hint ? (
            <Tooltip title={hint} custom={{ root: 'z-[60] max-w-[18rem]' }}>
              <InformationCircleIcon className="size-xxxs shrink-0 text-ctx-content-base" />
            </Tooltip>
          ) : null}
        </div>
        {action}
      </header>
      <div className="flex flex-col gap-mini px-xxs py-xxs">{children}</div>
    </section>
  );
}
